import { Col, Collapse, CollapseProps, Dropdown, GetProp, Input, MenuProps, message, Row, Select, Space, Upload, UploadProps } from "antd";
import "../styles.css";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import { CustomFile, General, ISunkhronosTexts, ISunkhronosUserCode } from "../utils";
import Flag from "../../../../common/languageSwitcher/flag";
import FAIcon from "../../../../common/FAIcon/FAIcon";
import UploadButton from "../../../../common/UploadButton/UploadButton";

interface Props {
    generalData: General;
    textsData: ISunkhronosTexts;
    userCode: ISunkhronosUserCode;
    updateTexts: (data: ISunkhronosTexts) => void;
    updateUserCode: (data: ISunkhronosUserCode) => void;
    updateData: (data: General) => void;
    updateFlag: (data: General) => void;
}

const GeneralCompanyDrawer = (props: Props) => {
    type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
    const intl = useIntl();

    const getBase64 = useCallback((img: FileType, callback: (url: string) => void) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result as string));
        reader.readAsDataURL(img);
    }, []);

    const beforeUpload = useCallback((file: FileType) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        if (!isJpgOrPng) {
            message.error(intl.formatMessage({ defaultMessage: "You can only load JPG/PNG file!" }));
            return Upload.LIST_IGNORE;
        }

        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error(intl.formatMessage({ defaultMessage: 'Image must be smaller than 2MB!' }));
            return Upload.LIST_IGNORE;
        }

        getBase64(file, (base64Url) => {
            const customFile: CustomFile = file as CustomFile;
            customFile.blob = base64Url;  // Ajouter la chaîne Base64 à la propriété blob
            props.updateData({ ...props.generalData, image: customFile });  // Met à jour les données avec le fichier contenant le blob
        });

        props.updateData({ ...props.generalData, image: file });
        return false;
    }, [getBase64, props, intl]);

    const flags: MenuProps['items'] = useMemo(() => [
        { key: 'FR', label: <Space><Flag countryCode="FR" /></Space>, onClick: () => props.updateFlag({ ...props.generalData, language: 'fr' }) },
        { key: 'EN', label: <Space><Flag countryCode="GB" /></Space>, onClick: () => props.updateFlag({ ...props.generalData, language: 'en' }) },
        { key: 'DE', label: <Space><Flag countryCode="DE" /></Space>, onClick: () => props.updateFlag({ ...props.generalData, language: 'de' }) }
    ], [props]);

    const items = useMemo<CollapseProps['items']>(() => [
        {
            key: '1',
            label: intl.formatMessage({ defaultMessage: "More options" }),
            children: (
                <Row className="inline flex-between">
                    <Col xs={{ span: 12 }} className="column">
                        <h2 style={{ marginTop: 0 }}><FAIcon prefix='fad' name='pen-line' /> <FormattedMessage defaultMessage="Texts" /></h2>
                        <Col xs={{ span: 18 }} className='column' style={{ gap: '10px' }}>
                            <Col className="flex-between">
                                <label><FormattedMessage defaultMessage="Project" /></label>

                                <Input
                                    value={props.textsData.project}
                                    onChange={(e) => props.updateTexts({ ...props.textsData, project: e.target.value })}
                                    style={{ width: "100px" }}
                                />
                            </Col>
                            <Col className="flex-between">
                                <label><FormattedMessage defaultMessage="Staff type" /></label>

                                <Input
                                    value={props.textsData.staff_type}
                                    onChange={(e) => props.updateTexts({ ...props.textsData, staff_type: e.target.value })}
                                    style={{ width: "100px" }}
                                />
                            </Col>
                        </Col>
                    </Col>
                    <Col xs={{ span: 12 }} className="column">
                        <h2 style={{ marginTop: 0 }}><FAIcon prefix='fad' name='input-numeric' /> <FormattedMessage defaultMessage="User code" /></h2>

                        <Col className='' xs={{ span: 24 }} style={{ display: 'flex', gap: '10px' }}>
                            <Select
                                prefix={<FAIcon prefix='fad' name='eye' />}
                                value={props.userCode.display}
                                onChange={(value) => props.updateUserCode({ ...props.userCode, display: value })}
                                style={{ width: '50%', marginRight: "3px" }}
                                options={[
                                    { value: -1, label: 'User Code ', disabled: true },
                                    { value: "hidden", label: intl.formatMessage({ defaultMessage: "Hidden" }) },
                                    { value: "read-only", label: intl.formatMessage({ defaultMessage: 'Read-only' }) },
                                    { value: "read-write", label: intl.formatMessage({ defaultMessage: 'Read-write' }) },
                                ]}
                            />
                            <Select
                                prefix={<FAIcon prefix='fad' name='left-right' />}
                                value={props.userCode.position}
                                onChange={(value) => props.updateUserCode({ ...props.userCode, position: value })}
                                style={{ width: '50%', marginRight: "3px" }}
                                options={[
                                    { value: -2, label: intl.formatMessage({ defaultMessage: 'Type' }), disabled: true },
                                    { value: -1, label: intl.formatMessage({ defaultMessage: 'Left' }) },
                                    { value: 0, label: intl.formatMessage({ defaultMessage: 'Hidden' }) },
                                    { value: 1, label: intl.formatMessage({ defaultMessage: 'Right' }) },
                                ]}
                                disabled={props.userCode.display === "hidden"}
                            />
                        </Col>
                    </Col>
                </Row>
            ),
        },
    ], [intl, props]);

    return (
        <>
            <Row gutter={[10, 10]}>
                <Col xs={{ span: 24 }}>
                    <h2><FAIcon prefix='fad' name='gear' /> <FormattedMessage defaultMessage="General" /></h2>
                    <Row className='flex-between centerHeight centerHeight'>
                        <UploadButton imageUpload={true} text={defineMessage({ defaultMessage: "Load logo" })} file={props.generalData.image} beforeUpload={beforeUpload} />

                        <Col xs={{ span: 19 }}>
                            <Input
                                style={{ marginBottom: 8 }}
                                placeholder='Nom'
                                value={props.generalData.name}
                                onChange={(e) => props.updateData({ ...props.generalData, name: e.target.value })}
                            />

                            <Col className='flex-between centerHeight'>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Col>
                                        <Select
                                            prefix={<FAIcon prefix='fad' name='building' />}
                                            value={props.generalData.type}
                                            onChange={(value) => props.updateData({ ...props.generalData, type: value })}
                                            style={{ minWidth: 120, marginRight: "3px" }}
                                            options={[
                                                { value: -1, label: intl.formatMessage({ defaultMessage: 'Type' }), disabled: true },
                                                { value: 2, label: intl.formatMessage({ defaultMessage: 'N-CLA' }) },
                                                { value: 3, label: intl.formatMessage({ defaultMessage: 'Normal' }) },
                                                { value: 4, label: intl.formatMessage({ defaultMessage: 'CCT' }) },
                                            ]}
                                        />
                                    </Col>
                                </div>
                                <Dropdown menu={{ items: flags }} trigger={['click']}>
                                    <a onClick={(e) => e.preventDefault()}>
                                        <Space>
                                            <Flag countryCode={props.generalData.language == "en" ? "GB" : props.generalData.language.toUpperCase()} />
                                        </Space>
                                    </a>
                                </Dropdown>
                            </Col>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row gutter={[10, 10]}>
                <Collapse className='full-width' items={items} ghost />
            </Row>
        </>
    );
};

export default GeneralCompanyDrawer;
